<script setup>
// Vue twin of <x-basket-voucher> (resources/views/components/basket-voucher.blade.php +
// App\View\Components\BasketVoucher). Same elements and class hooks, no styling of its
// own - components/basket-voucher.scss styles both implementations, which is what stops
// them drifting apart. See docs/blade-to-vue-components.md.
//
// Renders the <li> itself, exactly as the Blade component does, so callers put it in a
// <ul class="basket_vouchers">.
//
// Props mirror the `vouchers` entries in the basket payload BasketController@index
// returns (code, provider, covers, balance), so a payload voucher can be spread straight
// in with no reshaping.
//
// Removal is a single `remove` emit with no payload. The consumer knows the id; this
// component only knows it was asked to go.
import { computed, watch } from 'vue';
import Chip from '@/components/Chip.vue';
import SvgIcon from '@/components/SvgIcon.vue';
import { formatPrice } from '@/shared/formatPrice.js';

const props = defineProps({
    code:     { type: String, required: true },
    provider: { type: String, default: null },
    covers:   { type: String, default: null },
    balance:  { type: Number, default: null },
    // Already securing another booking when it was redeemed. Warned about in full at
    // the point of redemption; this row only reminds them which voucher it was.
    previouslyUsed: { type: Boolean, default: false },
});

const emit = defineEmits(['remove']);

// Watched rather than checked once at setup, matching BasketItem.vue: the basket is
// refetched wholesale after every add and remove, and the consumer keys its rows on the
// id, so Vue reuses this instance with fresh values rather than re-running setup.
watch(() => props.code, (code) => {
    if (! code || ! code.trim()) {
        console.error('[BasketVoucher] needs a code. Rendering the row without one.');
    }
}, { immediate: true });

// Mirrors the Blade class's constructor one-to-one: what this voucher is doing to the
// order, in display order. `covers` first, because it is the consequential one - removing
// a voucher that covers something takes that booking with it.
const meta = computed(() => {
    const parts = [];

    if (props.covers !== null) {
        parts.push(`Covers ${props.covers}`);
    }

    if (props.balance !== null) {
        parts.push(`${formatPrice(props.balance)} balance`);
    }

    // A product voucher that has not found its product yet: on the order and real, but
    // neither covering nor discounting anything.
    return parts.length ? parts : ['Not yet applied'];
});

// The visible control is an icon shared by every row, so without the code a screen reader
// announces several identical "remove" buttons.
const removeLabel = computed(() => `Remove voucher ${props.code} from your basket`);
</script>

<template>
    <li class="basket_voucher">
        <div class="basket_voucher_header">
            <p class="basket_voucher_code">{{ code }}</p>
            <!-- Its own button rather than CloseButton.vue, matching Blade: a card's
                 corner action carrying the delete icon. `plain` opts out of the
                 site-wide button skin. -->
            <button
                type="button"
                class="basket_voucher_remove plain"
                :aria-label="removeLabel"
                @click="emit('remove')"
            >
                <SvgIcon name="delete" />
            </button>
        </div>

        <Chip v-if="provider" :label="provider" icon="voucher" />

        <div class="basket_voucher_figures">
            <!-- Meta parts carry no separators of their own - basket-voucher.scss draws
                 one between adjacent spans. -->
            <p class="basket_voucher_meta">
                <span v-for="part in meta" :key="part">{{ part }}</span>
            </p>
        </div>

        <!-- Outside .basket_voucher_figures, which is a baseline-aligned row: this is
             its own line under the figures, not another figure beside them. -->
        <p v-if="previouslyUsed" class="basket_voucher_notice">
            Also used on another booking, please make sure the voucher will cover both
        </p>
    </li>
</template>
